import { Box, Typography, useTheme } from "@mui/material";
import {
  FilledTextFieldProps,
  OutlinedTextFieldProps,
  StandardTextFieldProps,
} from "@mui/material/TextField";
import React, { useState } from "react";
import { Controller } from "react-hook-form";
import { useGetCurrentBreakpoint } from "utilz/helpers";
import { InputRoot } from "./InputWithLabelRoot";
import { LabelWithInfo } from "./LabelWithInfo";

export type TextFieldProps =
  | StandardTextFieldProps
  | FilledTextFieldProps
  | OutlinedTextFieldProps;
export interface InputProps extends Omit<TextFieldProps, "size" | "type"> {
  size?: "large" | "medium" | "small";
  // biome-ignore lint/suspicious/noExplicitAny: pre-existing react-hook-form control; loose typing avoids generic ripple across all call sites.
  control?: any;
  name: string;
  label?: React.ReactNode;
  /** Optional help text shown via an info (i) icon next to the label. */
  tooltip?: React.ReactNode;
  error?: boolean;
  errorMessage?: string | null;
  required?: boolean;
  noBorder?: boolean;
  /**
   * set to false if fixed input is needed and a size prop can be specified
   */
  isResponsive?: boolean;
  hideHelperBox?: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: pre-existing loose sx passthrough.
  inputFieledSx?: any;
  allowOnlyNumbers?: boolean;
  type?: React.HTMLInputTypeAttribute | undefined;
  digitsAfterDecimal?: number;
}

export const Input = ({
  size = "small",
  control,
  name,
  label,
  tooltip,
  error = false,
  value,
  errorMessage,
  required = false,
  // variant = "outlined",
  isResponsive = false,
  hideHelperBox = false,
  noBorder = false,
  sx,

  inputFieledSx,
  ...rest
}: InputProps) => {
  const theme = useTheme();
  const breakpoint = useGetCurrentBreakpoint(theme);

  if (isResponsive) {
    if (breakpoint === "lg") {
      size = "large";
    } else if (breakpoint === "md") {
      size = "medium";
    } else {
      size = "small";
    }
  }

  return control ? (
    <Controller
      control={control}
      name={name}
      render={({ field: { ref, onChange, onBlur, value, name } }) => (
        <CustomInput
          ref={ref}
          onBlur={onBlur}
          value={value}
          name={name}
          onChange={onChange}
          size={size}
          errorMessage={errorMessage}
          error={error}
          label={label}
          tooltip={tooltip}
          required={required}
          isResponsive={isResponsive}
          hideHelperBox={hideHelperBox}
          noBorder={noBorder}
          sx={sx}
          inputFieledSx={inputFieledSx}
          {...rest}
        />
      )}
    />
  ) : (
    <CustomInput
      size={size}
      errorMessage={errorMessage}
      value={value}
      error={error}
      label={label}
      tooltip={tooltip}
      required={required}
      isResponsive={isResponsive}
      sx={sx}
      name={name}
      hideHelperBox={hideHelperBox}
      noBorder={noBorder}
      inputFieledSx={inputFieledSx}
      {...rest}
    />
  );
};

const CustomInput = ({
  size,
  errorMessage,
  value,
  error,
  label,
  tooltip,
  required,
  isResponsive,
  sx,
  name,
  hideHelperBox,
  onChange,
  ref,
  noBorder,
  inputFieledSx,
  allowOnlyNumbers,
  type,
  digitsAfterDecimal,
  ...rest
}: InputProps) => {
  const theme = useTheme();
  const [isFocused, setIsFocused] = useState(false);

  // Check if input has value
  const hasValue = value !== "" && value !== null && value !== undefined;

  // Extract InputProps and event handlers from rest to merge with our conditional logic
  const {
    InputProps: userInputProps,
    onFocus: userOnFocus,
    onBlur: userOnBlur,
    ...otherRest
  } = rest;

  // Extract startAdornment from user's InputProps
  const userStartAdornment = userInputProps?.startAdornment;

  // Show startAdornment only when input has value OR is focused
  const shouldShowAdornment =
    userStartAdornment && (!label || hasValue || isFocused);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (userOnFocus) {
      userOnFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    // Call the onBlur from Controller or user props
    if (userOnBlur) {
      userOnBlur(e);
    }
  };

  return (
    <InputRoot
      // disabled={true}
      type={type ? type : undefined}
      ref={ref}
      autoComplete="off"
      name={name}
      size={size}
      variant="outlined"
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={(e) => {
        if (!onChange) return;

        if (
          otherRest.inputProps?.maxLength &&
          e.target.value.length > otherRest.inputProps?.maxLength
        ) {
          return;
        }

        if (type === "number") {
          let value = e.target.value;
          if (
            value.length > 1 &&
            value.startsWith("0") &&
            !value.includes(".")
          ) {
            value = value.slice(1);
            e.target.value = `${value}`;
          }
        }

        if (allowOnlyNumbers) {
          const numberRegex = /^[0-9\b]+$/;
          if (e.target.value === "" || numberRegex.test(e.target.value)) {
            onChange(e);
          }
          return;
        }
        if (digitsAfterDecimal && type === "number" && digitsAfterDecimal > 0) {
          if (e.target.value.includes(".")) {
            const parts = e.target.value.split(".");
            if (parts[1] && parts[1].length > digitsAfterDecimal) {
              e.preventDefault();
              return;
            }
          }
        }

        // console.log(type);

        onChange(e);
      }}
      helperText={
        <Box
          sx={{
            mt: errorMessage ? "4px" : hideHelperBox ? "0px" : "4px",
          }}
          // height={hideHelperBox ? "0px" : "16px"}
        >
          <Typography variant="body-small" color="error">
            {errorMessage}
          </Typography>
        </Box>
      }
      // helperText={
      //   errorMessage ? (
      //     <Box
      //       height={hideHelperBox ? "0px" : "16px"}
      //       sx={{ bgcolor: "green" }}
      //     >
      //       <Typography variant="body-small" color="error">
      //         {errorMessage}
      //       </Typography>
      //     </Box>
      //   ) : null
      // }
      InputProps={{
        ...userInputProps,
        startAdornment: shouldShowAdornment ? userStartAdornment : undefined,
        sx: {
          "& .MuiInputBase-input.MuiInputBase-inputMultiline": {
            p: "0px",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            border: noBorder ? "none !important" : "",
            // backgroundColor: error ? theme.palette.error.tint : "transparent",
          },

          "&.MuiInput-underline.Mui-focused": {
            backgroundColor: theme.palette.neutral["005"],
            borderRadius: "4px",
            borderColor: theme.palette.primary.main,
            "&::after": {
              borderWidth: "1px ! important",
            },
            "&::before": {
              borderWidth: "1px ! important",
            },
          },

          "&.MuiInput-underline": {
            borderRadius: "4px",
            "&::after": {
              borderWidth: "1px ! important",
            },
            "&::before": {
              borderWidth: "1px ! important",
            },
          },

          ...inputFieledSx,
          ...userInputProps?.sx,
        },
      }}
      InputLabelProps={{
        // shrink: true,
        sx: {
          fontFamily: theme.typography["body-medium"].fontFamily,
          fontWeight: theme.typography["body-medium"].fontWeight,
          fontSize: theme.typography["body-medium"].fontSize,
          transform: {
            xs: "translate(14px,6px) scale(1)",
          },
          "&.MuiFormLabel-filled": {
            transform: "translate(14px, -9px) scale(.75)",
          },
          "&.Mui-focused": {
            color: theme.palette.purple.main,
            // fontWeight: 700,
            transform: "translate(14px, -9px) scale(.75)",
          },
        },
      }}
      // label={label}
      label={
        label && (
          <LabelWithInfo
            label={label}
            required={required}
            error={error}
            tooltip={tooltip}
          />
        )
      }
      sx={{
        // height: "32px",
        "& .MuiInputBase-root": {
          ...(isResponsive && { height: { sm: 32, md: 40, lg: 48, xl: 56 } }),
          height: "32px",
          ...theme.typography["body-medium"],
          overflow: "clip",
          backgroundColor: "white",
        },
        // "& .MuiInputLabel-root": {
        //   textTransform: "capitalize",
        // },
        "& .Mui-disabled": {
          backgroundColor: theme.palette.neutral["B-005"],
          "&.MuiFormLabel-root": {
            backgroundColor: "transparent",
          },
          "&.MuiFormHelperText-root": {
            backgroundColor: "transparent",
          },
        },
        "& .Mui-error": {
          backgroundColor: error ? theme.palette.error.tint : "transparent",
          "&.MuiFormLabel-root": {
            backgroundColor: "transparent",
          },
          "&.MuiFormHelperText-root": {
            backgroundColor: "transparent",
          },
        },
        "& .MuiFormHelperText-root": {
          mt: "0px",
        },
        ...sx,
      }}
      error={error}
      value={value}
      {...otherRest}
    />
  );
};
